import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  TrendingUp, DollarSign, Briefcase, Star, FileText, BarChart3,
  ArrowUpRight, ArrowDownRight, Activity
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnalyticsDashboard = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [analyticsRes, logsRes] = await Promise.all([
        axios.get(`${API}/analytics/user`),
        axios.get(`${API}/audit-logs?limit=20`)
      ]);
      setAnalytics(analyticsRes.data);
      setAuditLogs(logsRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN')}`;

  const getMonthlyData = () => {
    const data = analytics?.role === 'freelancer'
      ? analytics?.monthly_earnings || {}
      : analytics?.monthly_spending || {};
    return Object.entries(data)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6);
  };

  const monthlyData = analytics ? getMonthlyData() : [];
  const maxMonthlyValue = Math.max(...monthlyData.map(([, v]) => v), 1);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-lg">Loading analytics...</div>
        </div>
      </div>
    );
  }

  const isFreelancer = analytics?.role === 'freelancer';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-2" data-testid="analytics-title">
            Analytics Dashboard
          </h1>
          <p className="text-muted-foreground">
            {isFreelancer ? 'Track your earnings, gigs, and performance' : 'Monitor your spending, projects, and hiring activity'}
          </p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {isFreelancer ? (
            <>
              <Card data-testid="metric-earnings">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Earned</CardTitle>
                  <DollarSign className="h-4 w-4 text-secondary" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-heading font-bold text-primary">{formatCurrency(analytics?.total_earned)}</div>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <ArrowUpRight className="h-3 w-3 text-green-500" /> From {analytics?.completed_contracts || 0} completed jobs
                  </p>
                </CardContent>
              </Card>

              <Card data-testid="metric-gigs">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Gigs</CardTitle>
                  <Briefcase className="h-4 w-4 text-secondary" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-heading font-bold text-primary">{analytics?.gigs_count || 0}</div>
                  <p className="text-xs text-muted-foreground mt-1">{analytics?.active_contracts || 0} active contracts</p>
                </CardContent>
              </Card>

              <Card data-testid="metric-bids">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Bids</CardTitle>
                  <FileText className="h-4 w-4 text-secondary" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-heading font-bold text-primary">{analytics?.bids_count || 0}</div>
                  <p className="text-xs text-muted-foreground mt-1">Proposals submitted</p>
                </CardContent>
              </Card>

              <Card data-testid="metric-rating">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Rating</CardTitle>
                  <Star className="h-4 w-4 text-amber-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-heading font-bold text-primary">{analytics?.avg_rating?.toFixed(1) || '0.0'}</div>
                  <p className="text-xs text-muted-foreground mt-1">{analytics?.total_reviews || 0} reviews</p>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              <Card data-testid="metric-spent">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Spent</CardTitle>
                  <DollarSign className="h-4 w-4 text-secondary" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-heading font-bold text-primary">{formatCurrency(analytics?.total_spent)}</div>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <ArrowDownRight className="h-3 w-3 text-blue-500" /> Across all contracts
                  </p>
                </CardContent>
              </Card>

              <Card data-testid="metric-projects">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Projects Posted</CardTitle>
                  <FileText className="h-4 w-4 text-secondary" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-heading font-bold text-primary">{analytics?.projects_count || 0}</div>
                </CardContent>
              </Card>

              <Card data-testid="metric-active">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Contracts</CardTitle>
                  <Briefcase className="h-4 w-4 text-secondary" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-heading font-bold text-primary">{analytics?.active_contracts || 0}</div>
                </CardContent>
              </Card>

              <Card data-testid="metric-completed">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Completed</CardTitle>
                  <TrendingUp className="h-4 w-4 text-secondary" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-heading font-bold text-primary">{analytics?.completed_contracts || 0}</div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Monthly Chart */}
          <Card data-testid="monthly-chart">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Monthly {isFreelancer ? 'Earnings' : 'Spending'}
              </CardTitle>
              <CardDescription>Last 6 months overview</CardDescription>
            </CardHeader>
            <CardContent>
              {monthlyData.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No data available yet</p>
              ) : (
                <div className="space-y-3">
                  {monthlyData.map(([month, value]) => (
                    <div key={month} className="flex items-center gap-3">
                      <span className="text-sm font-mono text-muted-foreground w-20">{month}</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                        <div
                          className="h-full bg-secondary rounded-full flex items-center justify-end pr-2 transition-all duration-500"
                          style={{ width: `${Math.max((value / maxMonthlyValue) * 100, 8)}%` }}
                        >
                          <span className="text-xs font-medium text-white">{formatCurrency(value)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Activity Feed */}
          <Card data-testid="activity-feed">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Recent Activity
              </CardTitle>
              <CardDescription>Your latest actions on the platform</CardDescription>
            </CardHeader>
            <CardContent>
              {auditLogs.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No recent activity</p>
              ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
                      <div className="w-2 h-2 mt-2 rounded-full bg-secondary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{log.action.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-muted-foreground">
                          {log.resource_type} {log.resource_id ? `#${log.resource_id.slice(0, 8)}` : ''}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {new Date(log.created_at).toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Performance Summary */}
        <Card data-testid="performance-summary">
          <CardHeader>
            <CardTitle>Performance Summary</CardTitle>
            <CardDescription>Key metrics at a glance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {isFreelancer ? (
                <>
                  <div className="text-center p-4 rounded-lg bg-slate-50">
                    <div className="text-3xl font-heading font-bold text-primary">{analytics?.completed_contracts || 0}</div>
                    <div className="text-sm text-muted-foreground mt-1">Jobs Completed</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-slate-50">
                    <div className="text-3xl font-heading font-bold text-secondary">{formatCurrency(analytics?.total_earned)}</div>
                    <div className="text-sm text-muted-foreground mt-1">Total Revenue</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-slate-50">
                    <div className="text-3xl font-heading font-bold text-primary">{analytics?.gigs_count || 0}</div>
                    <div className="text-sm text-muted-foreground mt-1">Listed Gigs</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-slate-50">
                    <div className="text-3xl font-heading font-bold text-amber-600">
                      {analytics?.avg_rating?.toFixed(1) || '0.0'}
                      <Star className="inline h-5 w-5 ml-1" />
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">Avg. Rating</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center p-4 rounded-lg bg-slate-50">
                    <div className="text-3xl font-heading font-bold text-primary">{analytics?.projects_count || 0}</div>
                    <div className="text-sm text-muted-foreground mt-1">Total Projects</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-slate-50">
                    <div className="text-3xl font-heading font-bold text-secondary">{formatCurrency(analytics?.total_spent)}</div>
                    <div className="text-sm text-muted-foreground mt-1">Total Invested</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-slate-50">
                    <div className="text-3xl font-heading font-bold text-primary">{analytics?.active_contracts || 0}</div>
                    <div className="text-sm text-muted-foreground mt-1">In Progress</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-slate-50">
                    <div className="text-3xl font-heading font-bold text-primary">{analytics?.completed_contracts || 0}</div>
                    <div className="text-sm text-muted-foreground mt-1">Completed</div>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
